import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import DocumentPrintOptionsModal from '../components/DocumentPrintOptionsModal';

const isDarkModeLocal = () => localStorage.getItem('isDarkMode') === 'true' || window.matchMedia('(prefers-color-scheme: dark)').matches;

const PurchaseInvoiceForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const isNew = !id || String(id).toLowerCase() === 'new';
    
    const [loading, setLoading] = useState(false);
    const [printOptions, setPrintOptions] = useState({ open: false, context: null });
    const [showSupplierSearch, setShowSupplierSearch] = useState(false);
    const [showArticleSearch, setShowArticleSearch] = useState(false);
    const [selectedLineIndex, setSelectedLineIndex] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [articles, setArticles] = useState([]);
    const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
    const [articleSearchTerm, setArticleSearchTerm] = useState('');
    const [showSuspendedModal, setShowSuspendedModal] = useState(false);
    const [suspendedDocs, setSuspendedDocs] = useState([]);
    const [loadingSuspended, setLoadingSuspended] = useState(false);
    const [sendingSms, setSendingSms] = useState(false);
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [archiveFiles, setArchiveFiles] = useState([]);
    const [loadingArchive, setLoadingArchive] = useState(false);
    const [uploadingArchive, setUploadingArchive] = useState(false);
    const archiveFileInputRef = useRef(null);
    const cellRefs = useRef({});
    const [invoice, setInvoice] = useState({
        documentType: 'Fatura Compra',
        supplierCode: '',
        supplierId: null,
        supplierName: '',
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        supplierReference: '',
        warehouseId: 1,
        paymentMethod: 'Transferência Bancária',
        globalDiscountPercent: 0,
        notes: '',
        lines: [],
        subTotal: 0,
        taxTotal: 0,
        total: 0
    });

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(value || 0);
    };
    const getSupplierCode = (supplier) => supplier?.code || `SUP${String(supplier?.id || 0).padStart(6, '0')}`;
    const getOrderSourceType = (orderNumber) => String(orderNumber || '').startsWith('ORC-') ? 'quote' : 'order';
    const getPurchaseDocumentTypeFromOrder = (orderNumber) =>
        getOrderSourceType(orderNumber) === 'quote' ? 'Orçamento Fornecedor' : 'Encomenda Fornecedor';

    useEffect(() => {
        if (!isNew) {
            loadInvoice();
        } else {
            const source = new URLSearchParams(location.search).get('source');
            if (source === 'order') {
                setInvoice(prev => ({ ...prev, documentType: 'Encomenda Fornecedor' }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, location.search]);

    const loadInvoice = async () => {
        try {
            setLoading(true);
            const source = new URLSearchParams(location.search).get('source');

            if (source === 'order') {
                const res = await api.get(`/purchases/orders/${id}`);
                const data = res.data || {};
                setInvoice(prev => ({
                    ...prev,
                    documentType: getPurchaseDocumentTypeFromOrder(data.orderNumber),
                    supplierCode: data.supplier ? getSupplierCode(data.supplier) : (data.supplierId ? `SUP${String(data.supplierId).padStart(6, '0')}` : ''),
                    supplierId: data.supplierId || data.supplier?.id || null,
                    supplierName: data.supplier?.name || '',
                    invoiceNumber: data.orderNumber || '',
                    invoiceDate: data.orderDate?.split('T')[0] || '',
                    dueDate: data.expectedDeliveryDate?.split('T')[0] || '',
                    supplierReference: data.supplierReference || '',
                    warehouseId: data.warehouseId || 1,
                    paymentMethod: data.paymentMethod || prev.paymentMethod,
                    globalDiscountPercent: Number(data.globalDiscountPercent) || 0,
                    notes: data.notes || '',
                    lines: Array.isArray(data.lines) ? data.lines.map(line => ({
                        articleCode: line.article?.reference || '',
                        articleId: line.articleId || null,
                        description: line.description || line.article?.description || '',
                        quantity: Number(line.quantity) || 1,
                        availableQty: Number(line.article?.stock) || 0,
                        unitPrice: Number(line.unitPrice) || 0,
                        discountPercent: Number(line.discountPercent) || 0,
                        vatPercent: Number(line.vatPercent) || 23,
                        lineTotal: Number(line.lineTotal) || 0,
                        lineTax: Number(line.lineTax) || 0
                    })) : [],
                    subTotal: Number(data.subTotal) || 0,
                    taxTotal: Number(data.taxTotal) || 0,
                    total: Number(data.total) || 0
                }));
                return;
            }

            try {
                const res = await api.get(`/billing/purchases/${id}`);
                const data = res.data || {};
                setInvoice(prev => ({
                    ...prev,
                    documentType: 'Fatura Compra',
                    ...data,
                    supplierCode: data.supplier ? getSupplierCode(data.supplier) : (data.supplierId ? `SUP${String(data.supplierId).padStart(6, '0')}` : ''),
                    supplierId: data.supplierId || data.supplier?.id || null,
                    supplierName: data.supplier?.name || '',
                    invoiceDate: data.invoiceDate?.split('T')[0] || '',
                    dueDate: data.dueDate?.split('T')[0] || '',
                    lines: Array.isArray(data.lines) ? data.lines.map(line => ({
                        articleCode: line.article?.reference || '',
                        articleId: line.articleId || null,
                        description: line.description || line.article?.description || '',
                        quantity: Number(line.quantity) || 1,
                        availableQty: Number(line.article?.stock) || 0,
                        unitPrice: Number(line.unitPrice) || 0,
                        discountPercent: Number(line.discountPercent) || 0,
                        vatPercent: Number(line.vatPercent) || 23,
                        lineTotal: Number(line.lineTotal) || 0,
                        lineTax: Number(line.lineTax) || 0
                    })) : []
                }));
            } catch {
                const res = await api.get(`/purchases/orders/${id}`);
                const data = res.data || {};
                setInvoice(prev => ({
                    ...prev,
                    documentType: getPurchaseDocumentTypeFromOrder(data.orderNumber),
                    supplierCode: data.supplier ? getSupplierCode(data.supplier) : (data.supplierId ? `SUP${String(data.supplierId).padStart(6, '0')}` : ''),
                    supplierId: data.supplierId || data.supplier?.id || null,
                    supplierName: data.supplier?.name || '',
                    invoiceNumber: data.orderNumber || '',
                    invoiceDate: data.orderDate?.split('T')[0] || '',
                    dueDate: data.expectedDeliveryDate?.split('T')[0] || '',
                    supplierReference: data.supplierReference || '',
                    warehouseId: data.warehouseId || 1,
                    globalDiscountPercent: Number(data.globalDiscountPercent) || 0,
                    notes: data.notes || '',
                    lines: Array.isArray(data.lines) ? data.lines.map(line => ({
                        articleCode: line.article?.reference || '',
                        articleId: line.articleId || null,
                        description: line.description || line.article?.description || '',
                        quantity: Number(line.quantity) || 1,
                        availableQty: Number(line.article?.stock) || 0,
                        unitPrice: Number(line.unitPrice) || 0,
                        discountPercent: Number(line.discountPercent) || 0,
                        vatPercent: Number(line.vatPercent) || 23,
                        lineTotal: Number(line.lineTotal) || 0,
                        lineTax: Number(line.lineTax) || 0
                    })) : [],
                    subTotal: Number(data.subTotal) || 0,
                    taxTotal: Number(data.taxTotal) || 0,
                    total: Number(data.total) || 0
                }));
            }
        } catch (error) {
            toast.error('Erro ao carregar fatura');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const searchSupplier = async () => {
        try {
            const term = supplierSearchTerm || invoice.supplierCode || '';
            const res = await api.get(`/purchases/suppliers?search=${term}`);
            setSuppliers(res.data || []);
            setShowSupplierSearch(true);
        } catch (error) {
            toast.error('Erro ao buscar fornecedor');
            console.error(error);
        }
    };

    const selectSupplier = (supplier) => {
        setInvoice({
            ...invoice,
            supplierId: supplier.id,
            supplierCode: getSupplierCode(supplier),
            supplierName: supplier.name
        });
        setSupplierSearchTerm(getSupplierCode(supplier));
        setShowSupplierSearch(false);
        toast.success(`Fornecedor selecionado: ${supplier.name}`);
    };

    const setCellRef = (lineIndex, field) => (el) => {
        if (!el) return;
        cellRefs.current[`${lineIndex}-${field}`] = el;
    };

    const focusCell = (lineIndex, field) => {
        setTimeout(() => {
            const el = cellRefs.current[`${lineIndex}-${field}`];
            if (el && typeof el.focus === 'function') el.focus();
            if (el && typeof el.select === 'function') el.select();
        }, 0);
    };

    const searchArticles = async (lineIndex, termOverride = null) => {
        try {
            const term = termOverride != null ? termOverride : (articleSearchTerm || '');
            const res = await api.get(`/billing/articles?search=${encodeURIComponent(term)}`);
            setArticles(res.data || []);
            setSelectedLineIndex(lineIndex);
            setShowArticleSearch(true);
        } catch (error) {
            toast.error('Erro ao buscar artigos');
            console.error(error);
        }
    };

    const applyArticleToLine = (lineIndex, article) => {
        const newLines = [...invoice.lines];
        newLines[lineIndex] = {
            ...newLines[lineIndex],
            articleId: article.id,
            articleCode: article.reference,
            description: article.description,
            unitPrice: article.purchasePriceWithVat || article.purchasePriceNoVat || 0,
            vatPercent: article.vatPercent || 23,
            availableQty: article.stock || 0
        };

        const line = newLines[lineIndex];
        const subtotal = line.quantity * line.unitPrice;
        const discount = subtotal * (line.discountPercent / 100);
        const afterDiscount = subtotal - discount;
        const tax = afterDiscount * (line.vatPercent / 100);
        line.lineTotal = afterDiscount + tax;
        line.lineTax = tax;

        setInvoice({ ...invoice, lines: newLines });
        calculateTotals(newLines);
    };

    const selectArticle = (article) => {
        if (selectedLineIndex === null) return;

        applyArticleToLine(selectedLineIndex, article);
        setShowArticleSearch(false);
        setSelectedLineIndex(null);
        toast.success(`Artigo selecionado: ${article.description}`);
        focusCell(selectedLineIndex, 'description');
    };

    const resolveArticleCodeOnEnter = async (lineIndex) => {
        const raw = (invoice.lines?.[lineIndex]?.articleCode || '').toString().trim();
        if (!raw) {
            await searchArticles(lineIndex, '');
            return { matched: false };
        }

        try {
            const res = await api.get(`/billing/articles?search=${encodeURIComponent(raw)}`);
            const list = Array.isArray(res.data) ? res.data : [];
            setArticles(list);

            const match = list.find(a => (a?.reference || '').toString().trim().toLowerCase() === raw.toLowerCase());
            if (match) {
                applyArticleToLine(lineIndex, match);
                return { matched: true };
            }

            setArticleSearchTerm(raw);
            setSelectedLineIndex(lineIndex);
            setShowArticleSearch(true);
            toast.warning('Código não encontrado. Pesquise na lista.');
            return { matched: false };
        } catch (e) {
            console.error(e);
            await searchArticles(lineIndex, raw);
            return { matched: false };
        }
    };

    const handleLineKeyDown = async (e, lineIndex, field) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();

        if (field === 'articleCode') {
            const r = await resolveArticleCodeOnEnter(lineIndex);
            if (r?.matched) focusCell(lineIndex, 'description');
            return;
        }
        if (field === 'description') { focusCell(lineIndex, 'quantity'); return; }
        if (field === 'quantity') { focusCell(lineIndex, 'unitPrice'); return; }
        if (field === 'unitPrice') { focusCell(lineIndex, 'discountPercent'); return; }
        if (field === 'discountPercent') {
            const newIndex = invoice.lines.length;
            addLine();
            focusCell(newIndex, 'articleCode');
            return;
        }
    };

    const addLine = () => {
        setInvoice({
            ...invoice,
            lines: [...invoice.lines, {
                articleCode: '',
                articleId: null,
                description: '',
                quantity: 1,
                availableQty: 0,
                unitPrice: 0,
                discountPercent: 0,
                vatPercent: 23,
                lineTotal: 0,
                lineTax: 0
            }]
        });
    };

    const removeLine = (index) => {
        const newLines = invoice.lines.filter((_, i) => i !== index);
        setInvoice({ ...invoice, lines: newLines });
        calculateTotals(newLines);
    };

    const updateLine = (index, field, value) => {
        const newLines = [...invoice.lines];
        newLines[index][field] = value;
        
        const line = newLines[index];
        const subtotal = line.quantity * line.unitPrice;
        const discount = subtotal * (line.discountPercent / 100);
        const afterDiscount = subtotal - discount;
        const tax = afterDiscount * (line.vatPercent / 100);
        line.lineTotal = afterDiscount + tax;
        line.lineTax = tax;
        
        setInvoice({ ...invoice, lines: newLines });
        calculateTotals(newLines);
    };

    const handleDistributeCosts = () => {
        if (!invoice.lines.length) {
            toast.info('Adicione linhas antes de distribuir custos');
            return;
        }

        const amountInput = window.prompt('Valor total a distribuir pelas linhas (EUR):', '0');
        if (amountInput === null) return;

        const totalToDistribute = Number(String(amountInput).replace(',', '.'));
        if (!Number.isFinite(totalToDistribute) || totalToDistribute <= 0) {
            toast.error('Valor inválido para distribuição');
            return;
        }

        const mode = window.prompt('Tipo de distribuição: "proporcional" ou "igual"', 'proporcional');
        if (mode === null) return;

        const normalizedMode = mode.trim().toLowerCase();
        const byValue = normalizedMode !== 'igual';

        const eligibleIndexes = invoice.lines
            .map((line, idx) => ({ idx, qty: Number(line.quantity) || 0, base: (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0) }))
            .filter(x => x.qty > 0)
            .map(x => x.idx);

        if (!eligibleIndexes.length) {
            toast.error('Não existem linhas com quantidade válida');
            return;
        }

        const lines = [...invoice.lines];
        const baseTotal = eligibleIndexes.reduce((sum, idx) => sum + ((Number(lines[idx].quantity) || 0) * (Number(lines[idx].unitPrice) || 0)), 0);

        let distributed = 0;

        eligibleIndexes.forEach((lineIndex, position) => {
            const line = lines[lineIndex];
            const qty = Number(line.quantity) || 1;

            let share = 0;
            if (position === eligibleIndexes.length - 1) {
                share = totalToDistribute - distributed;
            } else if (byValue && baseTotal > 0) {
                const lineBase = (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
                share = Number(((totalToDistribute * lineBase) / baseTotal).toFixed(6));
            } else {
                share = Number((totalToDistribute / eligibleIndexes.length).toFixed(6));
            }

            distributed += share;
            const deltaPerUnit = share / qty;
            line.unitPrice = Number((Number(line.unitPrice || 0) + deltaPerUnit).toFixed(4));

            const subtotal = (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
            const discount = subtotal * ((Number(line.discountPercent) || 0) / 100);
            const afterDiscount = subtotal - discount;
            const tax = afterDiscount * ((Number(line.vatPercent) || 0) / 100);
            line.lineTotal = afterDiscount + tax;
            line.lineTax = tax;
        });

        setInvoice({ ...invoice, lines });
        calculateTotals(lines);
        toast.success(`Custos distribuídos (${formatCurrency(totalToDistribute)})`);
    };

    const calculateTotals = (lines) => {
        const subTotal = lines.reduce((sum, line) => sum + (line.quantity * line.unitPrice), 0);
        const taxTotal = lines.reduce((sum, line) => sum + line.lineTax, 0);
        const total = lines.reduce((sum, line) => sum + line.lineTotal, 0);
        
        setInvoice(prev => ({ ...prev, subTotal, taxTotal, total }));
    };

    const saveDocument = async ({ finalize = true } = {}) => {
        if (!invoice.supplierId) {
            toast.error('Selecione um fornecedor');
            return;
        }

        const isSupplierOrderDoc = invoice.documentType === 'Encomenda Fornecedor' || invoice.documentType === 'Orçamento Fornecedor';
        
        try {
            setLoading(true);
            const payload = {
                invoiceNumber: invoice.invoiceNumber || '',
                supplierId: Number(invoice.supplierId),
                warehouseId: invoice.warehouseId ? Number(invoice.warehouseId) : null,
                invoiceDate: invoice.invoiceDate,
                dueDate: invoice.dueDate || null,
                supplierReference: invoice.supplierReference || null,
                notes: invoice.notes || null,
                paymentTerms: 'Pronto Pagamento',
                paymentMethod: invoice.paymentMethod || null,
                autoUpdateStock: true,
                globalDiscountPercent: Number(invoice.globalDiscountPercent) || 0,
                lines: invoice.lines
                    .filter(line => line.articleId)
                    .map(line => ({
                        articleId: Number(line.articleId),
                        description: line.description || '',
                        quantity: Number(line.quantity) || 0,
                        unitPrice: Number(line.unitPrice) || 0,
                        discountPercent: Number(line.discountPercent) || 0,
                        vatPercent: Number(line.vatPercent) || 0,
                        vatRateId: null
                    }))
            };

            let savedId = id ? Number(id) : null;

            if (isSupplierOrderDoc) {
                const orderPayload = {
                    supplierId: Number(invoice.supplierId),
                    sourceType: invoice.documentType === 'Orçamento Fornecedor' ? 'quote' : 'order',
                    warehouseId: invoice.warehouseId ? Number(invoice.warehouseId) : null,
                    orderDate: invoice.invoiceDate,
                    expectedDeliveryDate: invoice.dueDate || null,
                    supplierReference: invoice.supplierReference || null,
                    notes: invoice.notes || null,
                    paymentTerms: 'Pronto Pagamento',
                    globalDiscountPercent: Number(invoice.globalDiscountPercent) || 0,
                    lines: invoice.lines
                        .filter(line => line.articleId)
                        .map(line => ({
                            articleId: Number(line.articleId),
                            description: line.description || '',
                            quantity: Number(line.quantity) || 0,
                            unitPrice: Number(line.unitPrice) || 0,
                            discountPercent: Number(line.discountPercent) || 0,
                            vatPercent: Number(line.vatPercent) || 0
                        }))
                };

                if (isNew) {
                    const createRes = await api.post('/purchases/orders', orderPayload);
                    savedId = createRes?.data?.order?.id || createRes?.data?.id || null;
                } else {
                    await api.put(`/purchases/orders/${id}`, orderPayload);
                    savedId = Number(id);
                }
            } else {
                if (isNew) {
                    const createRes = await api.post('/billing/purchases', payload);
                    savedId = createRes?.data?.id || createRes?.data?.purchase?.id || null;
                } else {
                    await api.put(`/billing/purchases/${id}`, payload);
                    savedId = Number(id);
                }
            }

            if (finalize) {
                if (!savedId) {
                    throw new Error('Não foi possível identificar o documento para finalizar.');
                }
                if (isSupplierOrderDoc) {
                    await api.post(`/purchases/orders/${savedId}/confirm`);
                } else {
                    await api.post(`/billing/purchases/${savedId}/post`);
                }
                toast.success('Documento gravado e finalizado com sucesso!');
                if (!isSupplierOrderDoc) {
                    setPrintOptions({
                        open: true,
                        context: {
                            kind: 'purchase',
                            docId: savedId,
                            docType: 'FC',
                            invoiceNumber: invoice.invoiceNumber,
                            title: 'Compra'
                        }
                    });
                } else {
                    navigate('/admin/purchases');
                }
                return;
            }

            toast.success('Documento gravado como suspenso.');
            if (isNew && savedId) {
                if (isSupplierOrderDoc) {
                    navigate(`/admin/purchases/${savedId}?source=order`);
                } else {
                    navigate(`/admin/purchases/${savedId}`);
                }
            }
        } catch (error) {
            toast.error('Erro ao gravar fatura');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const openSuspendedDocuments = async () => {
        try {
            setLoadingSuspended(true);
            const [invoiceRes, ordersRes] = await Promise.all([
                api.get('/billing/purchases?page=1&pageSize=200&status=Draft'),
                api.get('/purchases/orders?page=1&pageSize=200&status=Draft')
            ]);

            const invoiceDocs = (invoiceRes?.data?.purchases || []).map(d => ({ ...d, sourceKind: 'invoice' }));
            const orderDocs = (ordersRes?.data?.orders || []).map(d => ({
                ...d,
                sourceKind: 'order',
                invoiceDate: d.orderDate,
                invoiceNumber: d.orderNumber,
                supplier: { name: d.supplierName },
                total: d.total
            }));

            setSuspendedDocs([...invoiceDocs, ...orderDocs]);
            setShowSuspendedModal(true);
        } catch (error) {
            toast.error('Erro ao carregar documentos suspensos');
            console.error(error);
        } finally {
            setLoadingSuspended(false);
        }
    };

    const openSuspendedDocument = (docId) => {
        setShowSuspendedModal(false);
        const doc = suspendedDocs.find(d => d.id === docId);
        if (doc?.sourceKind === 'order') {
            navigate(`/admin/purchases/${docId}?source=order`);
        } else {
            navigate(`/admin/purchases/${docId}`);
        }
    };

    const loadArchiveFiles = async (purchaseId) => {
        try {
            setLoadingArchive(true);
            const res = await api.get(`/billing/purchases/${purchaseId}/digital-archive`);
            setArchiveFiles(res?.data?.files || []);
        } catch (error) {
            toast.error('Erro ao carregar arquivo digital');
            console.error(error);
        } finally {
            setLoadingArchive(false);
        }
    };

    const openArchiveModal = async () => {
        const purchaseId = id;
        if (!purchaseId) {
            toast.info('Guarde o documento para ativar o arquivo digital');
            return;
        }
        setShowArchiveModal(true);
        await loadArchiveFiles(purchaseId);
    };

    const triggerArchiveUpload = () => {
        if (!id) {
            toast.info('Guarde o documento para ativar o arquivo digital');
            return;
        }
        archiveFileInputRef.current?.click();
    };

    const handleArchiveFileSelected = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !id) return;

        try {
            setUploadingArchive(true);
            const formData = new FormData();
            formData.append('file', file);
            await api.post(`/billing/purchases/${id}/digital-archive`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Ficheiro arquivado com sucesso');
            await loadArchiveFiles(id);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Erro ao arquivar ficheiro');
            console.error(error);
        } finally {
            setUploadingArchive(false);
        }
    };

    const removeArchiveFile = async (fileName) => {
        if (!id) return;
        if (!window.confirm('Remover este ficheiro do arquivo digital?')) return;

        try {
            await api.delete(`/billing/purchases/${id}/digital-archive/${encodeURIComponent(fileName)}`);
            toast.success('Ficheiro removido');
            await loadArchiveFiles(id);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Erro ao remover ficheiro');
            console.error(error);
        }
    };

    const handleSendSms = async () => {
        if (!id) {
            toast.info('Guarde o documento antes de enviar SMS');
            return;
        }

        const suggested = `Documento de compra ${invoice.invoiceNumber || `#${id}`} registado com total ${Number(invoice.total || 0).toFixed(2)} EUR.`;
        const custom = window.prompt('Mensagem SMS (deixe em branco para usar o texto sugerido):', suggested);
        if (custom === null) return;

        try {
            setSendingSms(true);
            await api.post(`/billing/purchases/${id}/send-sms`, {
                message: custom.trim() || suggested
            });
            toast.success('SMS enviado com sucesso');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Falha no envio de SMS');
            console.error(error);
        } finally {
            setSendingSms(false);
        }
    };

    const handleCancel = async () => {
        if (!window.confirm('Tem certeza que deseja anular esta fatura?')) return;
        try {
            await api.delete(`/billing/purchases/${id}`);
            toast.success('Fatura anulada com sucesso!');
            navigate('/admin/purchases');
        } catch (error) {
            toast.error('Erro ao anular fatura');
        }
    };

    const handleDuplicate = () => {
        setInvoice({
            ...invoice,
            id: null,
            invoiceNumber: '',
            invoiceDate: new Date().toISOString().split('T')[0]
        });
        toast.info('Fatura duplicada. Altere os dados e grave.');
    };

    const handleConvertDocuments = async () => {
        try {
            const typePicked = window.prompt('Converter de que tipo? Escreva "encomenda", "orcamento" ou deixe vazio para ambos:', 'encomenda');
            if (typePicked === null) return;

            let sourceType = 'all';
            const normalized = typePicked.trim().toLowerCase();
            if (normalized.startsWith('enc')) sourceType = 'order';
            else if (normalized.startsWith('orc') || normalized.startsWith('orç')) sourceType = 'quote';

            const res = await api.get(`/billing/purchases/convertible-orders?sourceType=${sourceType}&page=1&pageSize=15`);
            const orders = res?.data?.orders || [];

            if (!orders.length) {
                toast.info('Sem documentos de fornecedor elegíveis para conversão');
                return;
            }

            const hint = orders
                .map(o => `#${o.id} ${o.orderNumber} [${o.sourceType === 'quote' ? 'ORÇ' : 'ENC'}] - ${o.supplierName} (${o.status})`)
                .join('\n');

            const picked = window.prompt(`Introduza o ID do documento fornecedor a converter:\n\n${hint}`);
            if (picked === null) return;

            const orderId = Number(String(picked).trim());
            if (!Number.isInteger(orderId) || orderId <= 0) {
                toast.error('ID inválido');
                return;
            }

            const convertRes = await api.post(`/billing/purchases/convert-from-order/${orderId}`);
            const newId = convertRes?.data?.purchaseId;
            if (!newId) {
                toast.error('Conversão sem retorno de documento');
                return;
            }

            toast.success('Documento convertido com sucesso');
            navigate(`/admin/purchases/${newId}`);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Erro ao converter documento');
            console.error(error);
        }
    };

    return (
        <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-card)' }}>
            <DocumentPrintOptionsModal
                open={!!printOptions.open}
                isDarkMode={localStorage.getItem('isDarkMode') === 'true' || window.matchMedia('(prefers-color-scheme: dark)').matches}
                context={printOptions.context}
                onClose={() => setPrintOptions({ open: false, context: null })}
                onDone={() => navigate('/admin/purchases')}
            />
            {/* Ribbon Menu - Estilo Office */}
            <div style={{
                backgroundColor: 'var(--info-bg)',
                borderBottom: '3px solid var(--border-color)',
                padding: '0.5rem 1rem',
                display: 'flex',
                gap: '1rem',
                overflowX: 'auto',
                position: 'sticky',
                top: 0,
                zIndex: 100,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
                {/* Opções */}
                <div style={ribbonGroupStyle}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button onClick={() => navigate('/admin/billing')} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>🚫</div>
                            <div style={ribbonLabelStyle}>Cancelar<br/>documento</div>
                        </button>
                        <button onClick={() => saveDocument({ finalize: true })} disabled={loading} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>💾</div>
                            <div style={ribbonLabelStyle}>Gravar<br/>documento</div>
                        </button>
                        <button onClick={handleCancel} disabled={isNew} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>❌</div>
                            <div style={ribbonLabelStyle}>Anular<br/>documento</div>
                        </button>
                        <button onClick={handleDuplicate} disabled={isNew} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>📑</div>
                            <div style={ribbonLabelStyle}>Duplicar</div>
                        </button>
                        <button onClick={handleDistributeCosts} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>💰</div>
                            <div style={ribbonLabelStyle}>Distribuir<br/>Custos</div>
                        </button>
                    </div>
                    <div style={ribbonGroupLabelStyle}>Opções</div>
                </div>

                <div style={ribbonSeparatorStyle}></div>

                {/* Documentos Suspensos */}
                <div style={ribbonGroupStyle}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button onClick={() => saveDocument({ finalize: false })} disabled={loading} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>📋</div>
                            <div style={ribbonLabelStyle}>Gravar Como<br/>Suspenso</div>
                        </button>
                        <button onClick={openSuspendedDocuments} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>📂</div>
                            <div style={ribbonLabelStyle}>Abrir<br/>Suspenso</div>
                        </button>
                    </div>
                    <div style={ribbonGroupLabelStyle}>Documentos Suspensos</div>
                </div>

                <div style={ribbonSeparatorStyle}></div>

                {/* Impressão */}
                <div style={ribbonGroupStyle}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button onClick={() => window.print()} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>🖨️</div>
                            <div style={ribbonLabelStyle}>Imprimir<br/>(F4)</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>👁️</div>
                            <div style={ribbonLabelStyle}>Ver antes<br/>Layout</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>✏️</div>
                            <div style={ribbonLabelStyle}>Editar<br/>email</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>📧</div>
                            <div style={ribbonLabelStyle}>Enviar<br/>email</div>
                        </button>
                        <button onClick={handleSendSms} disabled={sendingSms} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>📱</div>
                            <div style={ribbonLabelStyle}>{sendingSms ? 'A enviar...' : <>Enviar<br/>SMS</>}</div>
                        </button>
                    </div>
                    <div style={ribbonGroupLabelStyle}>Impressão</div>
                </div>

                <div style={ribbonSeparatorStyle}></div>

                {/* Conversão */}
                <div style={ribbonGroupStyle}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>🔗</div>
                            <div style={ribbonLabelStyle}>Referenciar<br/>documentos</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>↩️</div>
                            <div style={ribbonLabelStyle}>Devolução</div>
                        </button>
                        <button onClick={handleConvertDocuments} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>🔄</div>
                            <div style={ribbonLabelStyle}>Converter<br/>documentos</div>
                        </button>
                    </div>
                    <div style={ribbonGroupLabelStyle}>Conversão</div>
                </div>

                <div style={ribbonSeparatorStyle}></div>

                {/* Unitários */}
                <div style={ribbonGroupStyle}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>📋</div>
                            <div style={ribbonLabelStyle}>Copiar para<br/>linhas</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>🤖</div>
                            <div style={ribbonLabelStyle}>Assistente para<br/>inserir artigos</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>📥</div>
                            <div style={ribbonLabelStyle}>Importar</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>📊</div>
                            <div style={ribbonLabelStyle}>Importador</div>
                        </button>
                    </div>
                    <div style={ribbonGroupLabelStyle}>Unitários</div>
                </div>

                <div style={ribbonSeparatorStyle}></div>

                {/* Configurações */}
                <div style={ribbonGroupStyle}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>⚠️</div>
                            <div style={ribbonLabelStyle}>Avisos<br/>dinâmicos</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>📑</div>
                            <div style={ribbonLabelStyle}>Ordem dos<br/>campos</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>⚙️</div>
                            <div style={ribbonLabelStyle}>Configurar<br/>áreas</div>
                        </button>
                    </div>
                    <div style={ribbonGroupLabelStyle}>Configurações</div>
                </div>

                <div style={ribbonSeparatorStyle}></div>

                {/* Arquivo Digital */}
                <div style={ribbonGroupStyle}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button onClick={triggerArchiveUpload} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>📁</div>
                            <div style={ribbonLabelStyle}>Arquivar</div>
                        </button>
                        <button onClick={openArchiveModal} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>💾</div>
                            <div style={ribbonLabelStyle}>Arquivo<br/>Digital</div>
                        </button>
                        <button onClick={() => navigate('/admin/billing')} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>🚪</div>
                            <div style={ribbonLabelStyle}>Sair</div>
                        </button>
                    </div>
                    <div style={ribbonGroupLabelStyle}>Arquivo Digital</div>
                </div>
            </div>

            {/* Header */}
            <div style={{ padding: '1rem 2rem', backgroundColor: 'white', borderBottom: '1px solid var(--border-color)' }}>
                <h1 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
                    {isNew ? `📝 Novo ${invoice.documentType}` : `📄 ${invoice.documentType} ${invoice.invoiceNumber}`}
                </h1>
            </div>

            {/* Form */}
            <div style={{ padding: '2rem' }}>
                <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    {/* Header Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                            <label style={labelStyle}>Tipo Documento</label>
                            <select
                                value={invoice.documentType}
                                onChange={(e) => {
                                    const docType = e.target.value;
                                    setInvoice(prev => ({ ...prev, documentType: docType }));
                                }}
                                style={inputStyle}
                            >
                                <option>Fatura Compra</option>
                                <option>Encomenda Fornecedor</option>
                                <option>Orçamento Fornecedor</option>
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Código Fornecedor *</label>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                                <input
                                    type="text"
                                    value={invoice.supplierCode}
                                    onChange={(e) => {
                                        setInvoice({...invoice, supplierCode: e.target.value});
                                        setSupplierSearchTerm(e.target.value);
                                    }}
                                    style={{...inputStyle, flex: 1}}
                                    placeholder="Código"
                                />
                                <button onClick={searchSupplier} style={{ padding: '0.5rem 0.75rem', background: 'var(--info)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                    🔍
                                </button>
                            </div>
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                            <label style={labelStyle}>Nome Fornecedor</label>
                            <input type="text" value={invoice.supplierName} readOnly style={{...inputStyle, backgroundColor: 'var(--bg-card)'}} />
                        </div>
                        <div>
                            <label style={labelStyle}>Nº Fatura</label>
                            <input type="text" value={invoice.invoiceNumber} placeholder={isNew ? "Automático" : ""} readOnly={isNew} style={{...inputStyle, fontWeight: '600'}} />
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                        <div>
                            <label style={labelStyle}>Data Fatura</label>
                            <input type="date" value={invoice.invoiceDate} onChange={(e) => setInvoice({...invoice, invoiceDate: e.target.value})} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Data Vencimento</label>
                            <input type="date" value={invoice.dueDate} onChange={(e) => setInvoice({...invoice, dueDate: e.target.value})} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Ref. Fornecedor</label>
                            <input type="text" value={invoice.supplierReference} onChange={(e) => setInvoice({...invoice, supplierReference: e.target.value})} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Armazém</label>
                            <select value={invoice.warehouseId} onChange={(e) => setInvoice({...invoice, warehouseId: e.target.value})} style={inputStyle}>
                                <option value="1">Armazém Principal</option>
                                <option value="2">Armazém Secundário</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                        <div>
                            <label style={labelStyle}>Método Pag.</label>
                            <select value={invoice.paymentMethod} onChange={(e) => setInvoice({...invoice, paymentMethod: e.target.value})} style={inputStyle}>
                                <option>Transferência Bancária</option>
                                <option>Multibanco</option>
                                <option>Dinheiro</option>
                                <option>Cheque</option>
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Desc. Global %</label>
                            <input type="number" value={invoice.globalDiscountPercent} onChange={(e) => setInvoice({...invoice, globalDiscountPercent: parseFloat(e.target.value) || 0})} style={inputStyle} />
                        </div>
                        <div style={{ gridColumn: 'span 2' }} />
                    </div>

                    {/* Notes */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={labelStyle}>Observações</label>
                        <textarea value={invoice.notes} onChange={(e) => setInvoice({...invoice, notes: e.target.value})} style={{...inputStyle, minHeight: '80px', resize: 'vertical'}} placeholder="Observações adicionais..." />
                    </div>

                    {/* Lines Table */}
                    <div style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid var(--border-color)', borderRadius: '8px', backgroundColor: 'var(--bg-card)' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid var(--border-color)', background: 'var(--info-bg)' }}>
                                        <th style={{...tableHeaderStyle, width: '90px'}}>Referência</th>
                                        <th style={{...tableHeaderStyle, width: '420px'}}>Descrição</th>
                                        <th style={tableHeaderStyle}>QTD</th>
                                        <th style={tableHeaderStyle}>Qnt. Disponível</th>
                                        <th style={tableHeaderStyle}>P.Unit.</th>
                                        <th style={tableHeaderStyle}>Desc%</th>
                                        <th style={tableHeaderStyle}>Taxa IVA</th>
                                        <th style={tableHeaderStyle}>Total</th>
                                        <th style={{ padding: '10px', width: '50px' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoice.lines.map((line, i) => (
                                        <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                            <td style={{ padding: '8px' }}>
                                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                    <input
                                                        type="text"
                                                        value={line.articleCode || ''}
                                                        onChange={(e) => updateLine(i, 'articleCode', e.target.value)}
                                                        onKeyDown={(e) => handleLineKeyDown(e, i, 'articleCode')}
                                                        placeholder="Código"
                                                        ref={setCellRef(i, 'articleCode')}
                                                        style={{ ...tableCellInputStyle, flex: 1, minWidth: '60px' }}
                                                    />
                                                    <button
                                                        onClick={() => searchArticles(i)}
                                                        style={{
                                                            padding: '4px 8px',
                                                            background: 'var(--bg-tertiary)',
                                                            border: '1px solid var(--border-color)',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.75rem'
                                                        }}
                                                    >
                                                        🔍
                                                    </button>
                                                </div>
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                <input
                                                    type="text"
                                                    value={line.description || ''}
                                                    onChange={(e) => updateLine(i, 'description', e.target.value)}
                                                    onKeyDown={(e) => handleLineKeyDown(e, i, 'description')}
                                                    placeholder="Descrição"
                                                    ref={setCellRef(i, 'description')}
                                                    style={{ ...tableCellInputStyle, width: '100%' }}
                                                />
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                <input
                                                    type="number"
                                                    value={line.quantity}
                                                    onChange={(e) => updateLine(i, 'quantity', parseFloat(e.target.value) || 0)}
                                                    onKeyDown={(e) => handleLineKeyDown(e, i, 'quantity')}
                                                    ref={setCellRef(i, 'quantity')}
                                                    style={{...tableCellInputStyle, width: '60px', textAlign: 'right'}}
                                                />
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'right', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                                {(line.availableQty || 0).toFixed(2)}
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                <input
                                                    type="number"
                                                    value={line.unitPrice}
                                                    onChange={(e) => updateLine(i, 'unitPrice', parseFloat(e.target.value) || 0)}
                                                    onKeyDown={(e) => handleLineKeyDown(e, i, 'unitPrice')}
                                                    ref={setCellRef(i, 'unitPrice')}
                                                    style={{...tableCellInputStyle, width: '80px', textAlign: 'right'}}
                                                />
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                <input
                                                    type="number"
                                                    value={line.discountPercent}
                                                    onChange={(e) => updateLine(i, 'discountPercent', parseFloat(e.target.value) || 0)}
                                                    onKeyDown={(e) => handleLineKeyDown(e, i, 'discountPercent')}
                                                    ref={setCellRef(i, 'discountPercent')}
                                                    style={{...tableCellInputStyle, width: '60px', textAlign: 'right'}}
                                                />
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                <input
                                                    type="number"
                                                    value={line.vatPercent}
                                                    onChange={(e) => updateLine(i, 'vatPercent', parseFloat(e.target.value) || 0)}
                                                    style={{...tableCellInputStyle, width: '60px', textAlign: 'right'}}
                                                />
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600' }}>
                                                {formatCurrency(line.lineTotal)}
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'center' }}>
                                                <button onClick={() => removeLine(i)} style={{ background: 'var(--error)', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer', fontSize: '0.75rem' }}>
                                                    🗑️
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {invoice.lines.length === 0 && (
                                        <tr>
                                            <td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                                                Nenhuma linha adicionada. Clique em "+ Linha" para adicionar produtos.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <button onClick={addLine} style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: 'var(--bg-card)', color: 'var(--info)', border: '1px solid var(--info)', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600' }}>
                            + Linha
                        </button>
                    </div>

                    {/* Totals */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <div style={{ minWidth: '250px', padding: '1rem', backgroundColor: 'var(--info-bg)', borderRadius: '8px' }}>
                            <div style={totalRowStyle}>
                                <span>Subtotal:</span>
                                <span style={{ fontWeight: '600' }}>{formatCurrency(invoice.subTotal)}</span>
                            </div>
                            <div style={totalRowStyle}>
                                <span>IVA:</span>
                                <span style={{ fontWeight: '600' }}>{formatCurrency(invoice.taxTotal)}</span>
                            </div>
                            <div style={{...totalRowStyle, borderTop: '2px solid var(--border-color)', paddingTop: '0.5rem', marginTop: '0.5rem', fontSize: '1.1rem', fontWeight: '700'}}>
                                <span>Total:</span>
                                <span style={{ color: 'var(--success)' }}>{formatCurrency(invoice.total)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Supplier Search Modal */}
            {showSuspendedModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        width: '90%',
                        maxWidth: '900px',
                        maxHeight: '80vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div style={{
                            padding: '1.5rem',
                            borderBottom: '1px solid var(--border-color)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>📋 Documentos Suspensos (Draft)</h2>
                            <button
                                onClick={() => setShowSuspendedModal(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: 'var(--text-secondary)'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={{ flex: 1, overflow: 'auto', padding: '0 1rem 1rem' }}>
                            {loadingSuspended ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>A carregar...</div>
                            ) : suspendedDocs.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Sem documentos suspensos</div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Nº</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Fornecedor</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Data</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'right' }}>Total</th>
                                            <th style={{ padding: '0.75rem', width: '120px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {suspendedDocs.map(doc => (
                                            <tr key={doc.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                <td style={{ padding: '0.75rem' }}>{doc.invoiceNumber || `#${doc.id}`}</td>
                                                <td style={{ padding: '0.75rem' }}>{doc.supplier?.name || '-'}</td>
                                                <td style={{ padding: '0.75rem' }}>{doc.invoiceDate ? String(doc.invoiceDate).slice(0, 10) : '-'}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>{formatCurrency(doc.total)}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => openSuspendedDocument(doc.id)}
                                                        style={{
                                                            padding: '0.45rem 0.75rem',
                                                            background: 'var(--success)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.875rem'
                                                        }}
                                                    >
                                                        Abrir
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showArchiveModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        width: '90%',
                        maxWidth: '900px',
                        maxHeight: '80vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div style={{
                            padding: '1.5rem',
                            borderBottom: '1px solid var(--border-color)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>💾 Arquivo Digital - Compra</h2>
                            <button
                                onClick={() => setShowArchiveModal(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: 'var(--text-secondary)'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ padding: '1rem', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: '0.75rem' }}>
                            <button
                                onClick={triggerArchiveUpload}
                                disabled={uploadingArchive}
                                style={{
                                    padding: '0.55rem 0.9rem',
                                    background: 'var(--info)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem'
                                }}
                            >
                                {uploadingArchive ? 'A arquivar...' : '📁 Arquivar ficheiro'}
                            </button>
                            <button
                                onClick={() => id && loadArchiveFiles(id)}
                                disabled={loadingArchive}
                                style={{
                                    padding: '0.55rem 0.9rem',
                                    background: 'var(--info-bg)',
                                    color: 'var(--text-primary)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem'
                                }}
                            >
                                Atualizar
                            </button>
                        </div>

                        <div style={{ flex: 1, overflow: 'auto', padding: '0 1rem 1rem' }}>
                            {loadingArchive ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>A carregar...</div>
                            ) : archiveFiles.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Sem ficheiros arquivados</div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Ficheiro</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'right' }}>Tamanho</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Data</th>
                                            <th style={{ padding: '0.75rem', width: '180px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {archiveFiles.map(file => (
                                            <tr key={file.fileName} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                <td style={{ padding: '0.75rem' }}>{file.originalFileName || file.fileName}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'right' }}>{((Number(file.size) || 0) / 1024).toFixed(1)} KB</td>
                                                <td style={{ padding: '0.75rem' }}>{file.uploadedAt ? new Date(file.uploadedAt).toLocaleString('pt-PT') : '-'}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => window.open(file.url, '_blank')}
                                                        style={{
                                                            padding: '0.45rem 0.65rem',
                                                            background: 'var(--success)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.8rem',
                                                            marginRight: '0.4rem'
                                                        }}
                                                    >
                                                        Abrir
                                                    </button>
                                                    <button
                                                        onClick={() => removeArchiveFile(file.fileName)}
                                                        style={{
                                                            padding: '0.45rem 0.65rem',
                                                            background: 'var(--error)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.8rem'
                                                        }}
                                                    >
                                                        Remover
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showSupplierSearch && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        width: '90%',
                        maxWidth: '900px',
                        maxHeight: '80vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div style={{
                            padding: '1.5rem',
                            borderBottom: '1px solid var(--border-color)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>🤝 Pesquisar Fornecedor</h2>
                            <button
                                onClick={() => setShowSupplierSearch(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: 'var(--text-secondary)'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={{ padding: '1rem' }}>
                            <input
                                type="text"
                                placeholder="Pesquisar por nome, código ou NIF..."
                                value={supplierSearchTerm}
                                onChange={(e) => setSupplierSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && searchSupplier()}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '4px',
                                    fontSize: '1rem'
                                }}
                                autoFocus
                            />
                        </div>
                        <div style={{ flex: 1, overflow: 'auto', padding: '0 1rem 1rem' }}>
                            {suppliers.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                                    Digite para pesquisar fornecedores
                                </div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>Código</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>Nome</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>NIF</th>
                                            <th style={{ padding: '0.75rem', width: '100px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {suppliers
                                            .filter(supplier => {
                                                const term = supplierSearchTerm.toLowerCase().trim();
                                                if (!term) return true;
                                                return getSupplierCode(supplier).toLowerCase().includes(term)
                                                    || supplier.name?.toLowerCase().includes(term)
                                                    || supplier.nif?.toLowerCase().includes(term)
                                                    || supplier.id?.toString().includes(term);
                                            })
                                            .map(supplier => (
                                            <tr key={supplier.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                <td style={{ padding: '0.75rem', fontSize: '0.95rem', fontWeight: '700', color: 'var(--info-text)' }}>{getSupplierCode(supplier)}</td>
                                                <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{supplier.name}</td>
                                                <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{supplier.nif || '-'}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => selectSupplier(supplier)}
                                                        style={{
                                                            padding: '0.5rem 1rem',
                                                            background: 'var(--success)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.875rem'
                                                        }}
                                                    >
                                                        Selecionar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Article Search Modal */}
            {showArticleSearch && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        width: '90%',
                        maxWidth: '1000px',
                        maxHeight: '80vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div style={{
                            padding: '1.5rem',
                            borderBottom: '1px solid var(--border-color)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>📦 Pesquisar Artigo</h2>
                            <button
                                onClick={() => { setShowArticleSearch(false); setSelectedLineIndex(null); }}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: 'var(--text-secondary)'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={{ padding: '1rem' }}>
                            <input
                                type="text"
                                placeholder="Pesquisar por referência, descrição..."
                                value={articleSearchTerm}
                                onChange={(e) => setArticleSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && searchArticles(selectedLineIndex)}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '4px',
                                    fontSize: '1rem'
                                }}
                                autoFocus
                            />
                        </div>
                        <div style={{ flex: 1, overflow: 'auto', padding: '0 1rem 1rem' }}>
                            {articles.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                                    Digite para pesquisar artigos
                                </div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>Referência</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>Descrição</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>Preço Compra</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>Stock</th>
                                            <th style={{ padding: '0.75rem', width: '100px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {articles.map(article => (
                                            <tr key={article.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{article.reference}</td>
                                                <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{article.description}</td>
                                                <td style={{ padding: '0.75rem', fontSize: '0.875rem', textAlign: 'right' }}>{formatCurrency(article.purchasePriceWithVat || article.purchasePriceNoVat || 0)}</td>
                                                <td style={{ padding: '0.75rem', fontSize: '0.875rem', textAlign: 'right' }}>{article.stock || 0}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => selectArticle(article)}
                                                        style={{
                                                            padding: '0.5rem 1rem',
                                                            background: 'var(--success)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.875rem'
                                                        }}
                                                    >
                                                        Selecionar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <input
                ref={archiveFileInputRef}
                type="file"
                onChange={handleArchiveFileSelected}
                style={{ display: 'none' }}
            />
        </div>
    );
};

// Styles - Ribbon Menu
const ribbonButtonStyle = {
    padding: '0.5rem',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '70px',
    transition: 'all 0.2s',
    ':hover': {
        background: 'var(--bg-tertiary)',
        borderColor: 'var(--border-color)'
    }
};

const ribbonIconStyle = {
    fontSize: '1.5rem',
    marginBottom: '0.25rem'
};

const ribbonLabelStyle = {
    fontSize: '0.65rem',
    textAlign: 'center',
    lineHeight: '1.1',
    color: 'var(--text-primary)'
};

const ribbonGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem'
};

const ribbonGroupLabelStyle = {
    fontSize: '0.7rem',
    color: 'var(--text-secondary)',
    textAlign: 'center',
    paddingTop: '0.25rem',
    borderTop: '1px solid var(--border-color)'
};

const ribbonSeparatorStyle = {
    width: '1px',
    background: 'var(--bg-tertiary)',
    margin: '0.25rem 0'
};

const labelStyle = {
    fontSize: '0.7rem',
    color: 'var(--text-secondary)',
    fontWeight: '700',
    display: 'block',
    marginBottom: '0.25rem',
    textTransform: 'uppercase'
};

const inputStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    fontSize: '0.875rem'
};

const tableHeaderStyle = {
    padding: '10px',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: '700',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase'
};

const tableCellInputStyle = {
    width: '100%',
    padding: '0.25rem',
    border: '1px solid var(--border-color)',
    borderRadius: '3px',
    fontSize: '0.875rem'
};

const totalRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    fontSize: '0.875rem'
};

export default PurchaseInvoiceForm;
